import { zodResolver } from "@hookform/resolvers/zod";
import { type ReactNode, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Checkbox,
  CheckboxField,
  CheckboxGroup,
} from "@/components/ui/checkbox";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { mutationResponse } from "@/utils/trpc";

const schema = z.object({
  orgId: z.number(),
  email: z.email("Invalid email"),
  notificationTypes: z.string().array().min(1, "Required"),
});
type FormValues = z.infer<typeof schema>;

const notifications = [
  {
    id: "report",
    label: "Final reports",
    description: "Notification of final report (with PDF)",
  },
  {
    id: "received",
    label: "Accession received",
    description: "Accession has been received by lab",
  },
  {
    id: "comment",
    label: "Comments",
    description: "New comment added to accession",
  },
  {
    id: "hold",
    label: "Holds",
    description: "A hold was created or resolved",
  },
  {
    id: "additional_testing",
    label: "Additional testing",
    description: "Additional testing required (i.e. reflex)",
  },
  {
    id: "prelim_result",
    label: "Preliminary result",
    description: "Preliminary test result available",
  },
];

export function AddEmailNotificationDialog({
  children,
  notification,
  org,
}: {
  org: { id: number };
  notification?: { email: string; notificationTypes: string[] };
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const add = trpc.admin.org.addEmailNotification.useMutation();
  const update = trpc.admin.org.updateEmailNotification.useMutation();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      notificationTypes: [],
      ...notification,
    },
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (notification) {
      update.mutate(
        { ...values, previousEmail: notification.email },
        mutationResponse({
          errorTitle: "Error updating email notification",
          successMessage: "Email notification updated",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    } else {
      add.mutate(
        values,
        mutationResponse({
          errorTitle: "Error adding email notification",
          successMessage: "Email notification added",
          onSuccess() {
            setOpen(false);
          },
        }),
      );
    }
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>
              {notification ? "Edit" : "Add"} email notification
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <form
              onSubmit={handleSubmit(onSubmit)}
              id="add-email-notification-form"
            >
              <input
                type="hidden"
                {...register("orgId", { valueAsNumber: true })}
                value={org.id}
              />
              <div className="flex flex-col gap-4">
                <Controller
                  control={control}
                  name="email"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="email"
                      value={field.value ?? ""}
                      isInvalid={!!error}
                      validationBehavior="aria"
                    >
                      <Label>Email</Label>
                      <Input />
                      <FieldError>{error?.message}</FieldError>
                    </TextField>
                  )}
                />
                <Controller
                  control={control}
                  name="notificationTypes"
                  render={({ field, fieldState: { error } }) => (
                    <CheckboxGroup
                      value={field.value}
                      onChange={(e) => field.onChange(e)}
                      isInvalid={!!error}
                      validationBehavior="aria"
                    >
                      <Label>Notifications</Label>
                      {notifications.map((n) => (
                        <CheckboxField key={n.id}>
                          <Checkbox value={n.id}>{n.label}</Checkbox>
                          <Description>{n.description}</Description>
                        </CheckboxField>
                      ))}
                      <FieldError>{error?.message}</FieldError>
                    </CheckboxGroup>
                  )}
                />
              </div>
            </form>
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form="add-email-notification-form"
              isPending={add.isPending || update.isPending}
            >
              Submit
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
